/**
 * Where the book tutor sits on the reading page.
 *
 * Wide screens: a panel on the right, resizable from its left edge (drag, or
 * the arrow keys) and collapsible to a rail; both remembered. Phones: an "Ask
 * AI" button that opens the chat as a sheet from the bottom. Selecting a
 * passage opens it, so the selection is never added out of sight.
 */

import React, { useEffect, useRef, useState } from "react";
import { Box, Drawer, Fab, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

import { FONT } from "../Sessions/sessionParts";

const KEY = "reader-tutor";
export const DEFAULT_WIDTH = 400;
export const MIN_WIDTH = 320;
const maxWidth = () => Math.max(MIN_WIDTH, Math.min(760, Math.round(window.innerWidth * 0.6)));
export const clampWidth = (w) => Math.round(Math.min(maxWidth(), Math.max(MIN_WIDTH, Number(w) || DEFAULT_WIDTH)));

const readLayout = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || "{}");
    return { width: clampWidth(saved.width ?? DEFAULT_WIDTH), collapsed: !!saved.collapsed };
  } catch { return { width: DEFAULT_WIDTH, collapsed: false }; }
};
const saveLayout = (layout) => { try { localStorage.setItem(KEY, JSON.stringify(layout)); } catch { /* not remembered */ } };

function Handle({ width, onChange }) {
  const drag = (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    const move = (ev) => onChange(clampWidth(window.innerWidth - ev.clientX));
    const up = () => {
      document.body.style.userSelect = prev;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <Box role="separator" aria-orientation="vertical" aria-label="Resize the Ask AI panel" tabIndex={0}
      aria-valuenow={width} aria-valuemin={MIN_WIDTH} aria-valuemax={maxWidth()}
      onPointerDown={drag} onDoubleClick={() => onChange(DEFAULT_WIDTH)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") { e.preventDefault(); onChange(clampWidth(width + 24)); }
        if (e.key === "ArrowRight") { e.preventDefault(); onChange(clampWidth(width - 24)); }
      }}
      sx={{ width: 8, cursor: "col-resize", flexShrink: 0, position: "relative",
        "&:hover, &:focus-visible": { backgroundColor: "rgba(1,114,166,0.12)", outline: "none" },
        "&::after": { content: '""', position: "absolute", top: "50%", left: 3, width: 2, height: 32, mt: -2,
          borderRadius: 1, backgroundColor: "#cbd5e1" } }} />
  );
}

export default function ReaderTutorPanel({ mobile, openSignal, children }) {
  const [layout, setLayout] = useState(readLayout);
  const [sheet, setSheet] = useState(false);
  const first = useRef(true);
  const update = (patch) => setLayout((cur) => { const next = { ...cur, ...patch }; saveLayout(next); return next; });

  // A passage was selected: make sure the chat is showing.
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (!openSignal) return;
    if (mobile) setSheet(true);
    else setLayout((cur) => (cur.collapsed ? (saveLayout({ ...cur, collapsed: false }), { ...cur, collapsed: false }) : cur));
  }, [openSignal, mobile]);

  if (mobile) {
    return (
      <>
        {!sheet && (
          <Fab variant="extended" color="primary" onClick={() => setSheet(true)} aria-label="Ask AI"
            sx={{ position: "fixed", right: 16, bottom: 16, zIndex: 1100, textTransform: "none", fontFamily: FONT }}>
            <AutoAwesomeOutlinedIcon sx={{ mr: 1 }} /> Ask AI
          </Fab>
        )}
        <Drawer anchor="bottom" open={sheet} onClose={() => setSheet(false)} keepMounted
          PaperProps={{ sx: { height: "85vh", borderTopLeftRadius: 12, borderTopRightRadius: 12 } }}>
          <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 0.5, borderBottom: "1px solid #eef0f2" }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 15, flex: 1 }}>Ask AI</Typography>
            <IconButton onClick={() => setSheet(false)} aria-label="Close Ask AI"><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, "& > *": { height: "100%" } }}>{children}</Box>
        </Drawer>
      </>
    );
  }

  return (
    <Box data-testid="reader-tutor-panel" sx={{ display: "flex", flexShrink: 0, mt: "56px", height: "calc(100vh - 56px)",
      borderLeft: "1px solid #dadce0", backgroundColor: "#fff" }}>
      {layout.collapsed ? (
        <Paper elevation={0} sx={{ width: 40, display: "flex", flexDirection: "column", alignItems: "center", pt: 1, gap: 1 }}>
          <Tooltip title="Show Ask AI" placement="left">
            <IconButton size="small" onClick={() => update({ collapsed: false })} aria-label="Show Ask AI">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Typography sx={{ writingMode: "vertical-rl", fontFamily: FONT, fontSize: 13, color: "#6b7280", cursor: "pointer" }}
            onClick={() => update({ collapsed: false })}>Ask AI</Typography>
        </Paper>
      ) : (
        <>
          <Handle width={layout.width} onChange={(width) => update({ width })} />
          <Box sx={{ width: layout.width, display: "flex", flexDirection: "column", minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", pl: 1.5, pr: 0.5, py: 0.25, borderBottom: "1px solid #eef0f2" }}>
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: "#0172A6", mr: 1 }} />
              <Typography sx={{ fontFamily: FONT, fontWeight: 600, fontSize: 14, flex: 1 }}>Ask AI</Typography>
              <Tooltip title="Hide the panel">
                <IconButton size="small" onClick={() => update({ collapsed: true })} aria-label="Hide Ask AI">
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
          </Box>
        </>
      )}
    </Box>
  );
}
